import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faSearch,
  faChevronDown,
  faBell,
  faShoppingCart,
  faTimes,
  faBars,
  faHome,
  faUserCircle,
  faBoxOpen,
  faEllipsisVertical
} from "@fortawesome/free-solid-svg-icons";

import axios from 'axios';
import './Header.css';
import { useFilter } from '../context/FilterContext';
import { API_BASE_URL } from '../config';

// --- Sub-components for a cleaner structure ---

const NavLinks = React.memo(({ user, notificationCount, closeMobileMenu, isLandingOrProductPage, isAuthPage, isCartPage }) => (
  <>
    {!isLandingOrProductPage && (
      <Link to="/" className="nav-item" onClick={closeMobileMenu}>
        <FontAwesomeIcon icon={faHome} />
        <span>Home</span>
      </Link>
    )}
    {!isAuthPage && (
      <Link to="/auth" className="nav-item" onClick={closeMobileMenu}>
        <FontAwesomeIcon icon={faUserCircle} />
        <span>{user ? user.name : 'Sign In'}</span>
      </Link>
    )}
    {user?.role === 'buyer' && (
      <Link to="/orders" className="nav-item" onClick={closeMobileMenu}>
        <FontAwesomeIcon icon={faBoxOpen} />
        <span>Orders</span>
      </Link>
    )}
    {user?.role === 'vendor' && (
      <Link to="/vendor-dashboard/vendor-orders-summary" className="nav-item" onClick={closeMobileMenu}>
        <FontAwesomeIcon icon={faBell} />
        {notificationCount > 0 && <span className="notification-badge">{notificationCount}</span>}
        <span>Orders</span>
      </Link>
    )}
    {user?.role !== 'vendor' && !isCartPage && (
      <Link to="/cart" className="nav-item nav-item-cart" onClick={closeMobileMenu}>
        <FontAwesomeIcon icon={faShoppingCart} />
        <span>Cart</span>
      </Link>
    )}
  </>
));

const SearchAndFilter = React.memo(() => {
  const {
    searchTerm, setSearchTerm,
    showFilters, toggleFilters,
  } = useFilter();

  return (
    <div className="header-search-filter-container">
      <div className="search-bar">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          aria-label="Search products"
        />
        {searchTerm && (
          <button className="clear-search-button" onClick={() => setSearchTerm("")}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
      <button onClick={toggleFilters} className="filter-toggle-button" aria-expanded={showFilters}>
        <FontAwesomeIcon icon={faFilter} />
        <span>Filters</span>
        <FontAwesomeIcon icon={faChevronDown} className={`chevron ${showFilters ? "chevron-up" : ""}`} />
      </button>
    </div>
  );
});

const FilterSection = React.memo(() => {
  const {
    selectedCategory, setSelectedCategory,
    selectedBrand, setSelectedBrand,
    minPrice, setMinPrice,
    maxPrice, setMaxPrice,
    showFilters, clearFilters,
    categories, brands, sortBy, setSortBy,
    filteredProducts
  } = useFilter();

  return (
    <div className={`filter-section ${showFilters ? "show-filters" : ""}`}>
      <div className="filter-grid">
        <div className="filter-group">
          <label htmlFor="headerCategorySelect">Category</label>
          <select id="headerCategorySelect" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="filter-select">
            <option value="">All</option>
            {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="headerBrandSelect">Brand</label>
          <select id="headerBrandSelect" value={selectedBrand} onChange={(e) => setSelectedBrand(e.target.value)} className="filter-select">
            <option value="">All</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="headerSortSelect">Sort By</label>
          <select id="headerSortSelect" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="filter-select">
            <option value="default">Default</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="name_asc">Name: A to Z</option>
            <option value="name_desc">Name: Z to A</option>
          </select>
        </div>
        <div className="price-filter-group">
          <label>Price Range</label>
          <div className="price-inputs">
            <input type="number" placeholder="Min" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="price-input" />
            <span>-</span>
            <input type="number" placeholder="Max" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="price-input" />
          </div>
        </div>
      </div>
      <div className="filter-actions">
        <button onClick={clearFilters} className="clear-filters-button">Clear Filters</button>
        <div className="results-count">{filteredProducts.length} products found</div>
      </div>
    </div>
  );
});

// --- Main Header Component ---

const Header = ({ currentPath }) => {
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  
  const { showFilters, toggleFilters } = useFilter();
  const searchFilterRef = useRef(null);
  const dropdownRef = useRef(null);

  const showSearchBarAndFilters = currentPath === '/';

  const updateUser = useCallback(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      setUser(userData);
    } catch (error) {
      setUser(null);
    }
  }, []);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickAway = (event) => {
      if (
        showFilters &&
        searchFilterRef.current && !searchFilterRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        toggleFilters();
      }
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [showFilters, toggleFilters]);

  useEffect(() => {
    updateUser();
    window.addEventListener('storage', updateUser);
    window.addEventListener('userAuthChanged', updateUser);
    return () => {
      window.removeEventListener('storage', updateUser);
      window.removeEventListener('userAuthChanged', updateUser);
    };
  }, [updateUser]);

  const toggleMobileMenu = () => setMobileMenuOpen(prev => !prev);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('menu-open');
      window.isMobileMenuOpen = true;
    } else {
      document.body.classList.remove('menu-open');
      window.isMobileMenuOpen = false;
    }
    return () => {
      document.body.classList.remove('menu-open');
      window.isMobileMenuOpen = false;
    };
  }, [mobileMenuOpen]);

  // Listen for custom event to close menu from back button
  useEffect(() => {
    const handleCloseMenu = () => setMobileMenuOpen(false);
    window.addEventListener('closeMobileMenu', handleCloseMenu);
    return () => window.removeEventListener('closeMobileMenu', handleCloseMenu);
  }, []);

  return (
    <>
      <header className="site-header">
        <div className="header-main">
          <Link to="/" className="header-logo" onClick={closeMobileMenu}>
            <img
              src="/images/YetiDeals.png"
              alt="YetiDeals Logo"
              width="730"
              height="354"
              style={{
                width: 'auto',
                height: 'auto',
                maxWidth: '40px',
                maxHeight: 'auto',
                display: 'block'
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://www.yetideals.com/images/YetiDeals.png";
              }}
              loading="eager"
            />
          </Link>

          <div ref={searchFilterRef} style={{flexGrow: 1, display: 'flex'}}>
            {showSearchBarAndFilters && <SearchAndFilter />}
          </div>

          <nav className="header-nav-main">
            <NavLinks 
              user={user} 
              notificationCount={notificationCount} 
              closeMobileMenu={closeMobileMenu}
              isLandingOrProductPage={currentPath === '/' || currentPath.startsWith('/product/')}
              isAuthPage={currentPath === '/auth'}
              isCartPage={currentPath === '/cart'}
            />
          </nav>

          <button className="mobile-menu-toggle" onClick={toggleMobileMenu} aria-label="Toggle menu">
            {mobileMenuOpen ? <FontAwesomeIcon icon={faTimes} /> : <FontAwesomeIcon icon={faEllipsisVertical} />}
          </button>
        </div>

        <div ref={dropdownRef}>
          {showSearchBarAndFilters && <FilterSection />}
        </div>
      </header>

      {/* Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}></div>

      <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`}>
        <div className="mobile-nav-container">
          <NavLinks 
            user={user} 
            notificationCount={notificationCount} 
            closeMobileMenu={closeMobileMenu}
            isLandingOrProductPage={currentPath === '/' || currentPath.startsWith('/product/')}
            isAuthPage={currentPath === '/auth'}
            isCartPage={currentPath === '/cart'}
          />
        </div>
      </div>
    </>
  );
};
 
export default Header;